import {optionalMaterialInput, optionalPhysicalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import {vec3} from "#material/types/vector"
import {z} from "zod/v4-mini"
import {materialOutput} from "#material/nodes/base/material/validation/io"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {invertDisplacementDisabled} from "#material/nodes/base/output/validation"
import {versionChain} from "@cm/graph-v3"

export interface OutputMaterialSockets extends MaterialNodeSockets {
    inputs: {
        displacement: "vector" // found in inputs: vector,color,float; found in settings: vector,plain; inferred from data: float3,null
        surface: "closure" // found in inputs: closure,color; found in settings: plain; inferred from data: null
        volume: "closure" // found in inputs: -; found in settings: plain; inferred from data: null
    }
    settings: {
        name: "string"
        target: "string" // found in inputs: -; found in settings: string; inferred from data: string
        displacement: "vector" // found in inputs: vector,color,float; found in settings: vector,plain; inferred from data: float3,null
        displacementEnabled: "boolean"
        isActiveOutput: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean
        shadowCatcher: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean
    }
    outputs: {
        surface: "material"
    }
}

export const outputMaterialDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeOutputMaterial,
    },
    nodeClass: "OutputMaterial",
    versionChain: versionChain([invertDisplacementDisabled]),
    ui: {
        label: "Material Output",
        color: "#7c2a31",
        items: [
            {name: "surface", valueType: "closure", label: "Surface", input: true},
            {name: "volume", valueType: "closure", label: "Volume", input: true},
            {name: "displacement", valueType: "vector", label: "Displacement", input: true, setting: false},
            {name: "displacementEnabled", valueType: "boolean", label: "Use", input: false, setting: true, defaultValue: true},
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({surface: materialOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({
                surface: optionalPhysicalMaterialInput,
                volume: optionalMaterialInput,
                displacement: optionalMaterialInput,
            }),
            settings: z.strictObject({
                name: z.optional(z.string()),
                displacement: z.optional(vec3),
                displacementEnabled: z.optional(z.boolean()),
                isActiveOutput: z.optional(z.boolean()),
                shadowCatcher: z.optional(z.boolean()),
                target: z.optional(z.string()),
                thickness: z.transform(() => undefined),
                surface: z.transform(() => undefined),
                volume: z.transform(() => undefined),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<OutputMaterialSockets>
